import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PencilSquareIcon, PlayIcon, StopIcon, TrashIcon } from '@heroicons/react/24/solid'
import { clsx } from 'clsx'
import _ from 'lodash'

import { detachedUrlFromBot, urlFromBot } from 'src/libs/helper'
import { useGunbotAuth } from 'src/providers/BotLoginProvider'
import BaseButton from 'src/components/buttons/BaseButton'
import IconButton from 'src/components/buttons/IconButton'
import BaseTable, { Column, DetailedPanelProps } from 'src/components/tables/BaseTable'

import { AppDispatch } from 'src/store'
import {
  saveGunbotConfigAction,
  selectAllPnlHistory,
  selectBots,
  selectIsChangedGunbotConfig,
  selectIsGunbotLoading,
} from 'src/store/bots'
import { BotRes } from 'src/store/bots/bots.api'

import SettingDetail from './SettingDetail'

type ColumnItem = BotRes & {
  today?: number
  yesterday?: number
  week?: number
  year?: number
}

type Props = {
  onEditBot: (bot: ColumnItem) => void
  onDeleteBot: (bot: ColumnItem) => void
  onStopGunbot: (bot: ColumnItem) => void
  onStartGunbot: (bot: ColumnItem) => void
}

function BotsTableComponent({
  onEditBot,
  onDeleteBot,
  onStartGunbot,
  onStopGunbot,
}: Props) {
  const bots = useSelector(selectBots)
  const allPnlHistory = useSelector(selectAllPnlHistory)
  const handleGunbotAuth = useGunbotAuth()

  // === Detail Panel ===
  const DetailedPanel = useMemo(() => {
    const Panel = ({ rowData, index }: DetailedPanelProps<BotRes>) => {
      const url = urlFromBot(rowData)
      const detachedUrl = detachedUrlFromBot(rowData)
      return (
        <SettingDetail
          url={url}
          detachedUrl={detachedUrl}
          rowIndex={index}
          bot={rowData}
          onStartGunbot={onStartGunbot}
          onStopGunbot={onStopGunbot}
          onEditBot={onEditBot}
          onDeleteBot={onDeleteBot}
        />
      )
    }
    return Panel
  }, [onStartGunbot, onStopGunbot, onEditBot, onDeleteBot])

  // === Columns ===
  const columns: Column<ColumnItem>[] = useMemo(() => {
    // field is now optional for non-data columns
    const col = (
      label: string,
      field?: keyof ColumnItem,
      extra?: Partial<Column<ColumnItem>>
    ): Column<ColumnItem> => ({
      label,
      field: field as any, // safely cast for optional columns like "actions"
      headerClass: 'py-2 px-3 text-left truncate',
      cellClass: 'py-2 px-3 truncate',
      ...extra,
    })

    return [
      // This is the new dedicated column for the expander button.
      // It has no title and a very small width, creating a dedicated space
      // for the BaseTable's expand/collapse toggle.
      {
        label: '',
        headerClass: 'py-2 px-2 w-10', // Small width and padding
        cellClass: 'py-2 px-2 w-10',
      },
      col('Name', 'name', {
        render: (row: ColumnItem) => (
          <div className='flex items-center gap-2 min-w-0'>
            <span
              className={clsx('h-2 w-2 rounded-full flex-shrink-0', {
                'bg-success': row.alive,
                'bg-error': !row.alive,
              })}
            />
            <span className='truncate'>{row.name}</span>
          </div>
        ),
      }),
      col('Pairs', 'pairCount', {
        // Hides this column on mobile (screens smaller than `md`)
        headerClass: 'py-2 px-3 text-left truncate hidden md:table-cell',
        cellClass: 'py-2 px-3 truncate hidden md:table-cell',
        render: (row: ColumnItem) => <span>{row.pairCount}</span>,
      }),
      col('Today PnL', 'today', {
        render: (r: ColumnItem) =>
          _.isNil(r.today) ? '-' : Number(r.today.toPrecision(8)),
      }),
      col('Yesterday PnL', 'yesterday', {
        // Hides this column on mobile and tablet (screens smaller than `lg`)
        headerClass: 'py-2 px-3 text-left truncate hidden lg:table-cell',
        cellClass: 'py-2 px-3 truncate hidden lg:table-cell',
        render: (r: ColumnItem) =>
          _.isNil(r.yesterday) ? '-' : Number(r.yesterday.toPrecision(8)),
      }),
      col('Weekly PnL', 'week', {
        // Hides this column on mobile and tablet (screens smaller than `lg`)
        headerClass: 'py-2 px-3 text-left truncate hidden lg:table-cell',
        cellClass: 'py-2 px-3 truncate hidden lg:table-cell',
        render: (r: ColumnItem) =>
          _.isNil(r.week) ? '-' : Number(r.week.toPrecision(8)),
      }),
      col('Yearly PnL', 'year', {
        // Hides this column on mobile and tablet (screens smaller than `lg`)
        headerClass: 'py-2 px-3 text-left truncate hidden lg:table-cell',
        cellClass: 'py-2 px-3 truncate hidden lg:table-cell',
        render: (r: ColumnItem) =>
          _.isNil(r.year) ? '-' : Number(r.year.toPrecision(8)),
      }),
      col('On/Off', undefined, {
        render: (bot: ColumnItem) => {
          const { start, loggedIn } = bot
          return start ? (
            <IconButton
              size='md'
              color='error'
              disabled={!loggedIn}
              onClick={() => onStopGunbot(bot)}
            >
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton
              size='md'
              color='success'
              disabled={!loggedIn}
              onClick={() => onStartGunbot(bot)}
            >
              <PlayIcon />
            </IconButton>
          )
        },
      }),
      col('Login', undefined, {
        render: (bot: ColumnItem) =>
          bot.loggedIn ? (
            <BaseButton
              className='rounded-md'
              color='error'
              size='xs'
              fontSize='2xs'
              variant='outlined'
              disabled={!bot.alive}
              onClick={() => handleGunbotAuth(bot, false)}
            >
              Log out
            </BaseButton>
          ) : (
            <BaseButton
              className='rounded-md'
              color='primary'
              size='xs'
              fontSize='2xs'
              variant='outlined'
              disabled={!bot.alive}
              onClick={() => handleGunbotAuth(bot, true)}
            >
              Log in
            </BaseButton>
          ),
      }),
      col('Actions', undefined, {
        // Hides this column on mobile (screens smaller than `md`)
        headerClass: 'py-2 px-3 text-left truncate hidden md:table-cell',
        cellClass: 'py-2 px-3 truncate hidden md:table-cell',
        render: (bot: ColumnItem) => (
          <div className='flex items-center gap-2 min-w-0 flex-shrink'>
            <SaveChangesBtn url={urlFromBot(bot)} />
            <IconButton
              color='warning'
              onClick={() => onEditBot(bot)}
              className='min-h-5 min-w-5'
            >
              <PencilSquareIcon />
            </IconButton>
            <IconButton
              color='error'
              onClick={() => onDeleteBot(bot)}
              className='min-h-5 min-w-5'
            >
              <TrashIcon />
            </IconButton>
          </div>
        ),
      }),
    ]
  }, [onEditBot, onDeleteBot, onStartGunbot, onStopGunbot, handleGunbotAuth])

  // === Table Data ===
  const tableData: ColumnItem[] = useMemo(() => {
    return bots.map((bot) => {
      const url = urlFromBot(bot)
      const pnl = allPnlHistory[url]
      return pnl
        ? {
            ...bot,
            today: pnl.today.pnl,
            yesterday: pnl.yesterday.pnl,
            week: pnl.weekAgo.pnl,
            year: pnl.yearAgo.pnl,
          }
        : bot
    })
  }, [bots, allPnlHistory])

  // === Render ===
  return (
    <div className='w-full overflow-x-hidden'>
      <div className='w-full overflow-hidden'>
        <BaseTable
          columns={columns}
          data={tableData}
          className='mt-4 w-full'
          border
          DetailedPanel={DetailedPanel}
          defaultActiveDetailIndex={-1}
          fullDetailedPanel
          style={{
            width: '100%',
            tableLayout: 'fixed',
          }}
        />
      </div>
    </div>
  )
}

export default BotsTableComponent

// === Save button ===
export function SaveChangesBtn({ url }: { url: string }) {
  const changed = useSelector(selectIsChangedGunbotConfig(url))
  const loading = useSelector(selectIsGunbotLoading(url))
  const dispatch = useDispatch<AppDispatch>()

  const handleSaveConfig = useCallback(() => {
    dispatch(saveGunbotConfigAction(url))
  }, [dispatch, url])

  if (!changed) return null

  return (
    <BaseButton
      size='xs'
      fontSize='xs'
      variant='outlined'
      className={clsx('rounded-md', {
        'animate-save-changes shadow-md shadow-yellow-300': !loading,
      })}
      disabled={loading}
      color='primary'
      onClick={handleSaveConfig}
    >
      SAVE
    </BaseButton>
  )
}